import { Box, Grid, Typography } from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CustomizedBox } from "../../../components/Custom/CustomizedBox";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import SalesByCustomerTable from "../../../components/Table/DocumentTable/SalesByCustomerTable";
import NewReportDateFilter from "../../../components/UI/NewReportDateFilter";
import { getSalesReportByCustomerReport } from "../../../features/Sales/SalesReport/sales-report-action";
import { dateToUnix } from "../../../utils/date-converter";
import { exportCSVParams } from "../../../utils/filterparams";
import { salesReportActions } from "../../../features/Sales/SalesReport/sales-report-slice";

const SalesByCustomer = () => {
  const { salesReportByCustomer, isLoading } = useSelector(
    (state) => state.salesReport
  );
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gridRef = useRef();

  const breadcrumbs = [
    {
      name: t("sales.index"),
      to: "/sales",
    },
    {
      name: t("sales.report.index"),
      to: "/sales/report",
    },
    {
      name: t("sales.report.salesByCustomer"),
    },
  ];

  const { control, getValues, reset, setValue } = useForm({
    defaultValues: {
      dateType: "lastWeek",
      date: new Date(moment().startOf("day").subtract(6, "day")),
      dateTo: new Date(moment().endOf("day")),
    },
  });

  const onBtnExport = useCallback(async () => {
    let startDate = getValues("date");
    let endDate = getValues("dateTo");

    const params = exportCSVParams(
      t("sales.report.salesByCustomer"),
      startDate,
      endDate
    );
    gridRef.current.api.exportDataAsCsv(params);
    enqueueSnackbar("นำออกรายงานยอดขายตามลูกค้าสำเร็จ", {
      variant: "success",
    });
  }, [enqueueSnackbar, getValues, t]);

  const onFilterReset = () => {
    if (gridRef) {
      gridRef.current.api.setFilterModel({});
    }
    reset({
      dateType: "lastWeek",
      date: new Date(moment().startOf("day").subtract(6, "day")),
      dateTo: new Date(moment().endOf("day")),
    });
    dispatch(
      getSalesReportByCustomerReport(
        {
          startRow: 0,
          endRow: Math.pow(10, 10),
          filterModel: {
            issue_date: {
              filter: parseInt(
                dateToUnix(new Date(moment().startOf("day").subtract(6, "day")))
              ),
              filterTo: parseInt(dateToUnix(new Date(moment().endOf("day")))),
              filterType: "number",
              type: "inRange",
            },
            render_status: {
              values: ["finished"],
              filterType: "set",
            },
          },
          sortModel: [],
        },
        enqueueSnackbar
      )
    );
  };

  const getFilteredSalesByCustomer = useCallback(() => {
    let startDate = getValues("date");
    let endDate = getValues("dateTo");

    dispatch(
      getSalesReportByCustomerReport(
        {
          startRow: 0,
          endRow: Math.pow(10, 10),
          filterModel: {
            issue_date: {
              filter: parseInt(dateToUnix(startDate)),
              filterTo: parseInt(dateToUnix(endDate)),
              filterType: "number",
              type: "inRange",
            },
            render_status: {
              values: ["finished"],
              filterType: "set",
            },
          },
          sortModel: [],
        },
        enqueueSnackbar
      )
    );
  }, [dispatch, enqueueSnackbar, getValues]);

  useEffect(() => {
    return () => dispatch(salesReportActions.resetSalesByCustomer());
  }, [dispatch]);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">
          {t("sales.report.salesByCustomer")}
        </Typography>
        <CustomizedButton
          title="นำออกข้อมูล"
          variant="contained"
          onClick={onBtnExport}
        />
      </Box>
      <CustomizedBox>
        <Typography ml={1} mb={2} fontWeight="bold">
          ตัวกรองแสดงผล
        </Typography>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <NewReportDateFilter
                t={t}
                control={control}
                setValue={setValue}
                getValues={getValues}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2} lg={2} alignSelf="center">
              <CustomizedButton
                title={t("button.submitFilter")}
                variant="contained"
                onClick={getFilteredSalesByCustomer}
                disabled={isLoading.salesReportByCustomer}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2} lg={2} alignSelf="center">
              <CustomizedButton
                title={t("button.resetFilter")}
                variant="outlined"
                onClick={onFilterReset}
                disabled={isLoading.salesReportByCustomer}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </CustomizedBox>
      {salesReportByCustomer && salesReportByCustomer.length > 0 && (
        <SalesByCustomerTable
          gridRef={gridRef}
          rowData={salesReportByCustomer}
        />
      )}
    </>
  );
};

export default SalesByCustomer;
